import { Building2, Check, Plus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useSidebar } from "@/components/ui/sidebar";
import { AccountIcon } from "@/components/shell/AccountIcon";
import { cn } from "@/lib/utils";
import { useOptionalAccountWorkspace, type WorkspaceAccount } from "@/lib/account-workspace";
import { api } from "@/lib/api";
import { resolveAccountPlanEyebrow, type Plan, type Subscription } from "@/lib/subscriptions";
import { ContextHeaderTrigger } from "./ContextHeaderTrigger";

function AccountSwitcherEyebrow({
  accountId,
  accessKind,
}: {
  accountId: string | null;
  accessKind: "membership" | "authorization" | null;
}) {
  const [label, setLabel] = useState(accessKind === "authorization" ? "代管访问…" : "加载套餐…");

  useEffect(() => {
    if (!accountId) {
      setLabel("从下方选择要进入的账户");
      return;
    }

    if (accessKind === "authorization") {
      setLabel("代管访问");
      return;
    }

    let cancelled = false;
    setLabel("加载套餐…");

    Promise.all([
      api.fetch<{ plans: Plan[] }>("/plans"),
      api.fetchForAccount<{ subscriptions: Subscription[] }>(
        accountId,
        `/accounts/${accountId}/subscriptions`,
      ),
    ])
      .then(([plansPayload, subsPayload]) => {
        if (cancelled) return;
        setLabel(resolveAccountPlanEyebrow(subsPayload.subscriptions, plansPayload.plans));
      })
      .catch(() => {
        if (!cancelled) setLabel("未开通套餐");
      });

    return () => {
      cancelled = true;
    };
  }, [accountId, accessKind]);

  return <>{label}</>;
}

function AccountMenuRow({
  item,
  active,
  onSelect,
  badge,
}: {
  item: WorkspaceAccount;
  active: boolean;
  onSelect: () => void;
  badge?: string;
}) {
  return (
    <DropdownMenuItem
      className={cn("cursor-pointer", active && "bg-accent")}
      onSelect={onSelect}
    >
      <AccountIcon name={item.name} iconKey={item.icon_key} color={item.color} size="sm" />
      <span className="min-w-0 flex-1 truncate font-heading">{item.name}</span>
      {badge ? <span className="shrink-0 text-[10px] text-muted-foreground">{badge}</span> : null}
      {active ? <Check className="size-4 shrink-0" /> : null}
    </DropdownMenuItem>
  );
}

/** Account-level context header: current account name + plan/expiry, switchable. */
export function AccountSwitcher() {
  const workspace = useOptionalAccountWorkspace();
  const { isMobile, setOpenMobile } = useSidebar();
  const navigate = useNavigate();

  const accounts = workspace?.accounts ?? [];
  const { membershipAccounts, authorizedAccounts } = useMemo(() => {
    const membership: WorkspaceAccount[] = [];
    const authorized: WorkspaceAccount[] = [];
    for (const item of accounts) {
      if (item.access_kind === "authorization") authorized.push(item);
      else membership.push(item);
    }
    return { membershipAccounts: membership, authorizedAccounts: authorized };
  }, [accounts]);

  if (!workspace) return null;

  const { account, accountId, accessKind, switchAccount, openCreateAccount } = workspace;

  if (accounts.length === 0 && !account) {
    return (
      <Button
        type="button"
        variant="ghost"
        className="h-14 w-full justify-start gap-2.5 rounded-xl px-2 text-left hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
        onClick={() => {
          setOpenMobile(false);
          navigate("/onboarding");
        }}
      >
        <Plus className="size-4" />
        创建账户
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <ContextHeaderTrigger
          aria-label="切换账户"
          title={account?.name ?? "选择账户"}
          subtitle={
            <AccountSwitcherEyebrow
              accountId={account ? accountId || String(account.id) : null}
              accessKind={accessKind}
            />
          }
          icon={
            account ? (
              <AccountIcon
                name={account.name}
                iconKey={account.icon_key}
                color={account.color}
                size="lg"
                className="size-8 rounded-lg"
              />
            ) : (
              <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted">
                <Building2 className="size-4 text-muted-foreground" />
              </span>
            )
          }
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-(--radix-dropdown-menu-trigger-width) min-w-64"
        align="start"
        side={isMobile ? "bottom" : "right"}
      >
        <DropdownMenuLabel className="text-xs text-muted-foreground">我的账户</DropdownMenuLabel>
        {membershipAccounts.length === 0 ? (
          <div className="px-2 py-1.5 text-xs text-muted-foreground">暂无自有账户</div>
        ) : (
          membershipAccounts.map((item) => (
            <AccountMenuRow
              key={`m-${item.id}`}
              item={item}
              active={item.id === account?.id && accessKind === "membership"}
              onSelect={() => {
                setOpenMobile(false);
                switchAccount(item.id);
              }}
            />
          ))
        )}

        {authorizedAccounts.length > 0 ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs text-muted-foreground">代管账户</DropdownMenuLabel>
            {authorizedAccounts.map((item) => (
              <AccountMenuRow
                key={`a-${item.id}`}
                item={item}
                active={item.id === account?.id && accessKind === "authorization"}
                badge={item.permission === "maintain" ? "代管" : "只读"}
                onSelect={() => {
                  setOpenMobile(false);
                  switchAccount(item.id);
                }}
              />
            ))}
          </>
        ) : null}

        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="cursor-pointer"
          onSelect={() => {
            setOpenMobile(false);
            openCreateAccount();
          }}
        >
          <Plus className="size-4" />
          创建新账户
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
